import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'
import { initTheme } from './theme'
import { AppLayout } from './layout/AppLayout'
import { AuthProvider, AuthGate } from './auth'
import { LangProvider, initLang } from './i18n'
import { ProfileLangSync } from './layout/ProfileLangSync'
import { ErrorBoundary } from './layout/ErrorBoundary'
import { OrgProvider } from './org'
import { ViewProvider } from './context/ViewContext'
import { MapsPage } from './maps/MapsPage'
import { ModelsPage } from './models/ModelsPage'
import { BuoysPage } from './buoys/BuoysPage'
import { NavwarnPage } from './navwarn/NavwarnPage'
import { RulesPage } from './rules/RulesPage'
import { QuotasPage } from './quotas/QuotasPage'
import { PricesPage } from './prices/PricesPage'
import { ReportsPage } from './reports/ReportsPage'
import { ImageryPage } from './imagery/ImageryPage'
import { SatellitesSection } from './satellites/SatellitesSection'
import { SettingsPage } from './settings/SettingsPage'
import 'maplibre-gl/dist/maplibre-gl.css'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false } },
})

initTheme()
// Язык — до первого кадра, по запомненному набору домена. Иначе английский
// домен показывал бы русский кабинет и переписывал его на глазах (см. siteCache).
initLang()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Внешняя граница — на случай падения самого каркаса (внутренняя, вокруг
          раздела, живёт в AppLayout). Без них любое исключение оставляло бы
          пустой экран без единого слова о причине. */}
      {/* Язык — НАД маршрутами и над ViewContext: он нужен раньше маршрутов
          (экран входа, границы ошибок) и меняется независимо от вида. Смена
          языка не должна ронять запомненный регион карты, который живёт
          уровнем ниже. */}
      <LangProvider>
      <ErrorBoundary>
        <BrowserRouter basename="/app">
          <AuthProvider>
            <AuthGate>
              <ProfileLangSync />
              <OrgProvider>
                {/* Общий контекст (дата·глубина·вид) — НАД маршрутами, чтобы
                    переживать переходы между разделами. Внутри Router: читает URL. */}
                <ViewProvider>
                <Routes>
                  <Route element={<AppLayout />}>
                    <Route index element={<Navigate to="/maps" replace />} />
                    <Route path="maps/:layer?" element={<MapsPage />} />
                    <Route path="models/:model?/:species?" element={<ModelsPage />} />
                    <Route path="buoys" element={<BuoysPage />} />
                    <Route path="rules" element={<RulesPage />} />
                    <Route path="navwarn" element={<NavwarnPage />} />
            <Route path="quotas" element={<QuotasPage />} />
                    <Route path="prices" element={<PricesPage />} />
                    <Route path="reports" element={<Navigate to="/reports/ais-gaps" replace />} />
                    <Route path="reports/ais-gaps" element={<ReportsPage />} />
                    <Route path="imagery" element={<ImageryPage />} />
                    <Route path="satellites" element={<SatellitesSection />} />
                    <Route path="settings/*" element={<SettingsPage />} />
                  </Route>
                  {/* Ловушка. Незнакомый адрес — это НЕ пустой экран: <Routes>
                      без совпадения рисует ничего, и человек получает ровно фон
                      страницы, неотличимый от упавшего кабинета. Так выглядел
                      вход по ссылке из письма, пока она вела на /app/auth/verify
                      (бой 29.08.2026). Сама ссылка чинится в auth.tsx; ловушка
                      закрывает КЛАСС: любой чужой или устаревший адрес. */}
                  <Route path="*" element={<Navigate to="/maps" replace />} />
                </Routes>
                </ViewProvider>
              </OrgProvider>
            </AuthGate>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
      </LangProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
